import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { API_URL } from '../config';
import { getProgress, saveProgress } from '../services/ProgressService';

const HLSPlayer = ({ videoUrl, markers = [], postHistory, selectedSection, videoId, section }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [snapshots, setSnapshots] = useState({});
  const hlsRef = useRef(null); // Reference to the Hls instance

  const [playedTime, setPlayedTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playedTimeRef = useRef(0);
  const videoTimeRef = useRef(0);
  const totalTimeRef = useRef(0);

  const timeTrackerIntervalRef = useRef(null);
  const saveTickRef = useRef(0);   // throttle progress saves to ~every 10s
  const resumedRef = useRef(false); // resume only once per loaded video

  // Resume position + percent are saved to the additive /api/progress overlay.
  // Both are guarded (real learner video only) and best-effort, so they can
  // never affect playback or the existing VideoHistory tracking.
  const canTrackProgress = () => videoId && section !== 'Intranet';

  // Media-time actually played since the last beat. Completion is judged on
  // this, NOT on the furthest position reached — otherwise dragging the scrubber
  // to the end would score 100% without watching anything. Seeks are excluded by
  // only counting small forward advances; fast playback still counts honestly.
  const watchedPendingRef = useRef(0);
  const lastTimeRef = useRef(0);

  const accumulateWatched = () => {
    const video = videoRef.current;
    if (!video) return;
    const now = video.currentTime || 0;
    const delta = now - lastTimeRef.current;
    // A 1s tick advances ~1s of media (up to 4s at 4× playback). Anything
    // bigger is a seek, anything negative is a rewind — neither earns credit.
    if (delta > 0 && delta <= 4) watchedPendingRef.current += delta;
    lastTimeRef.current = now;
  };

  const saveProgressNow = () => {
    const video = videoRef.current;
    if (!canTrackProgress() || !video) return;
    const pos = video.currentTime || 0;
    const dur = video.duration || 0;
    if (dur <= 0) return;
    const watched = watchedPendingRef.current;
    watchedPendingRef.current = 0;
    saveProgress({
      contentType: 'video',
      contentId: videoId,
      position: pos,
      duration: dur,
      watchedDelta: watched,
    });
  };

  const submitTime = (time, videoTime) => {
    console.log("times", time, videoTime, totalTimeRef.current);
    postHistory(time, videoTime, totalTimeRef.current, videoId);
    setPlayedTime(0);
    playedTimeRef.current = 0;
    videoTimeRef.current = 0;
    totalTimeRef.current = 0;
  }

  useEffect(() => {
    console.log("videoId",videoId);
  }, [videoId]);

  useEffect(() => {
    
    const video = videoRef?.current;

    // New video loaded -> allow one resume seek.
    resumedRef.current = false;
    const resumeIfNeeded = async () => {
      if (resumedRef.current || !canTrackProgress() || !video) return;
      resumedRef.current = true;
      const p = await getProgress('video', videoId);
      if (!p) return;
      const dur = video.duration || p.duration || 0;
      // Resume only if there's meaningful progress and we're not at the end.
      if (p.position > 3 && dur > 0 && p.position < dur - 5) {
        try {
          video.currentTime = p.position;
        } catch (e) {
          /* seek not ready; ignore */
        }
      }
    };

    const startTrackingTime = () => {
      if (!timeTrackerIntervalRef.current) {
        timeTrackerIntervalRef.current = setInterval(() => {
          setPlayedTime((prevTime) => {
            const newTime = prevTime + 1;
            videoTimeRef.current = videoRef.current.currentTime;
            totalTimeRef.current = videoRef.current.duration;
            playedTimeRef.current = newTime; // Update the ref
            return newTime;
          });
          // Bank the media time this tick actually played (excludes seeks).
          accumulateWatched();
          // Persist resume position roughly every 10s of playback.
          saveTickRef.current += 1;
          if (saveTickRef.current % 10 === 0) saveProgressNow();
        }, 1000);
      }
    };
  
    const stopTrackingTime = () => {
      if (timeTrackerIntervalRef.current) {
        clearInterval(timeTrackerIntervalRef.current);
        timeTrackerIntervalRef.current = null; // Reset the interval ref
      }
    };
  
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopTrackingTime();
        submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime when the tab becomes hidden
        saveProgressNow();
      } else if (document.visibilityState === "visible" && isPlaying) {
        startTrackingTime();
      }
    };

    const handleBeforeUnload = () => {
      stopTrackingTime();
      submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime before the page unloads
      saveProgressNow();
    };
  
    if (Hls.isSupported() && videoUrl) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
  
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsVideoReady(true);
      });

      if(section === "Intranet") return;
  
      video.addEventListener("playing", () => {
        setIsPlaying(true);
        // Re-baseline so the gap while paused/buffering isn't credited.
        lastTimeRef.current = video.currentTime || 0;
        startTrackingTime();
      });

      video.addEventListener("pause", () => {
        setIsPlaying(false);
        stopTrackingTime();
        accumulateWatched();
        saveProgressNow();
      });

      // A seek must not bank the jump — re-baseline at the new position.
      video.addEventListener("seeked", () => {
        lastTimeRef.current = video.currentTime || 0;
      });

      video.addEventListener("ended", () => {
        stopTrackingTime();
        accumulateWatched();
        submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime when the video ends
        // Reaching the end is not itself proof of watching — the server scores
        // this on the watched seconds banked above, like every other beat.
        saveProgressNow();
      });

      video.addEventListener("loadedmetadata", () => {
        lastTimeRef.current = video.currentTime || 0;
        resumeIfNeeded();
      });

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        stopTrackingTime();
        submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime when the component unmounts or video URL changes
        saveProgressNow();
        hls.destroy();
  
        video.removeEventListener("playing", startTrackingTime);
        video.removeEventListener("pause", stopTrackingTime);
        video.removeEventListener("ended", stopTrackingTime);
  
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    } else if (video && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        setIsVideoReady(true);
        resumeIfNeeded();
      });

      video.addEventListener("playing", () => {
        setIsPlaying(true);
        startTrackingTime();
      });

      video.addEventListener("pause", () => {
        setIsPlaying(false);
        stopTrackingTime();
        saveProgressNow();
      });
  
      video.addEventListener("ended", () => {
        stopTrackingTime();
        submitTime(playedTimeRef.current); // Call submitTime when the video ends
      });
  
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("beforeunload", handleBeforeUnload);
  
      return () => {
        stopTrackingTime();
        submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime when the component unmounts or video URL changes
  
        video.removeEventListener("playing", startTrackingTime);
        video.removeEventListener("pause", stopTrackingTime);
        video.removeEventListener("ended", stopTrackingTime);
  
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  
    return () => {
      stopTrackingTime();
      submitTime(playedTimeRef.current, videoTimeRef.current); // Call submitTime during final cleanup
      saveProgressNow();
    };
  }, [videoUrl, markers, postHistory, videoId, section]);
  
  
  

  const captureSnapshot = (marker) => {
    const videoElement = videoRef.current;
    if (!videoElement || isNaN(marker.startTime)) return;

    // Set the video to the specific marker time
    videoElement.currentTime = marker.startTime;

    const onSeeked = () => {
      // Slight delay to ensure the frame is stable before capturing the snapshot
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas && videoElement) {
          const context = canvas.getContext('2d');

          // Set canvas size to video dimensions
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          // Draw the current frame of the video on the canvas
          context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

          // Convert canvas content to a base64 image
          const imageUrl = canvas.toDataURL('image/png');
          setSnapshots((prevSnapshots) => ({
            ...prevSnapshots,
            [marker.startTime]: imageUrl,
          }));

          // Remove event listener after snapshot is taken
          videoElement.removeEventListener('seeked', onSeeked);
        }
      }, 200); // Optional small delay for frame stability
    };

    // Add event listener for `seeked` to ensure the snapshot is taken after seeking
    videoElement.addEventListener('seeked', onSeeked, { once: true });
  };

  const handleSelectMarker = (startTime) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;

      videoRef.current.addEventListener(
        'seeked',
        () => {
          videoRef.current.play().catch((e) => {
            console.error('Error trying to play the video:', e);
          });
        },
        { once: true }
      );
    }
  };

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play().catch((e) => {
        console.error('Error trying to play the video:', e);
      });
    }
  };

  function formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    return `${hours > 0 ? hours + ':' : ''}${
        minutes.toString().padStart(2, '0')
    }:${remainingSeconds.toString().padStart(2, '0')}`;
}


  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        ref={videoRef}
        controls
        width="100%"
        height="100%"
        style={{ width: '100%', height: '100%', background: 'black' }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {!isVideoReady && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
          onClick={handlePlay}
        >
          Click to Play
        </div>
      )}
      {
        markers.length > 0 && (
          <div className="marker-list" style={{ background: 'white', display: 'flex', gap: '30px',  cursor: 'pointer', padding: '10px' }}>
        {markers.map((marker, index) => (
          <div key={index} className="marker-item" onClick={() => handleSelectMarker(marker.startTime)}>
            
           
              <img
                src={`${API_URL}/api/video/chapter/thumbnail/${marker.image}`}
                alt={' '}
                style={{ width: '100px', height: '60px' }}
              />
              <p style={{color: 'black', fontSize: '12px', margin: 0, marginLeft: '1px' }}>{formatTime(marker.startTime)}</p>
              <div style={{ color: 'black', fontSize: '10px' }}>{marker.title}</div>
          </div>
        ))}
      </div>
        )
      }
    </div>
  );
};

export default HLSPlayer;
